import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Senha alterada com sucesso.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 rounded-sm border-slate-200">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-[#0052CC]" />
        <h2 className="font-display font-black text-lg tracking-tight text-slate-950">Alterar senha</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">Mínimo de 8 caracteres. Você continuará conectado após a troca.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        <div>
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Senha atual</Label>
          <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password" className="mt-1 h-11 rounded-sm" data-testid="current-password-input" />
        </div>
        <div>
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Nova senha</Label>
          <Input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password" className="mt-1 h-11 rounded-sm" data-testid="new-password-input" />
        </div>
        <div>
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Confirmar nova senha</Label>
          <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password" className="mt-1 h-11 rounded-sm" data-testid="confirm-password-input" />
        </div>
        <Button type="submit" disabled={saving}
          className="h-11 px-6 rounded-sm bg-[#0052CC] hover:bg-[#003D99] text-white font-medium"
          data-testid="change-password-submit">
          {saving ? "Salvando…" : "Alterar senha"}
        </Button>
      </form>
    </Card>
  );
}

function ChangeEmailCard() {
  const { user, setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-email", { current_password: password, new_email: email });
      setUser((prev) => ({ ...prev, email: data.email }));
      toast.success(`E-mail de acesso alterado para ${data.email}.`);
      setPassword("");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 rounded-sm border-slate-200">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-[#0052CC]" />
        <h2 className="font-display font-black text-lg tracking-tight text-slate-950">Alterar e-mail de acesso</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        E-mail atual: <span className="font-mono text-slate-700" data-testid="current-email">{user?.email}</span>
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="change-email-form">
        <div>
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Novo e-mail</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="novo@empresa.com" className="mt-1 h-11 rounded-sm" data-testid="new-email-input" />
        </div>
        <div>
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Senha atual (confirmação)</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password" className="mt-1 h-11 rounded-sm" data-testid="email-password-input" />
        </div>
        <Button type="submit" disabled={saving}
          className="h-11 px-6 rounded-sm bg-[#0052CC] hover:bg-[#003D99] text-white font-medium"
          data-testid="change-email-submit">
          {saving ? "Salvando…" : "Alterar e-mail"}
        </Button>
      </form>
    </Card>
  );
}

export default function Account() {
  const { user } = useAuth();
  return (
    <div className="p-6 lg:p-10 max-w-3xl" data-testid="account-page">
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Segurança</div>
      <h1 className="font-display font-black text-3xl tracking-tighter text-slate-950 mb-1">Minha Conta</h1>
      <p className="text-sm text-slate-600 mb-8 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        {user?.name} · perfil <span className="font-mono uppercase">{user?.role}</span>
      </p>
      <div className="space-y-6">
        <ChangePasswordCard />
        <ChangeEmailCard />
      </div>
    </div>
  );
}
